import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, CalendarPlus, Trash2, UserRound, ShieldAlert, Phone, Mail, MessageCircle,
  Link2, ExternalLink, Pencil, Plus, X, StickyNote, History as HistoryIcon,
  CircleHelp, ContactRound, CalendarDays, Activity, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn, initialsOf, dateLocale } from "@/lib/utils";
import { fmtDate, relDays } from "@/pages/Reperes";
import { translateFr as tfr } from "@/i18n";
import {
  FirstContactDialog, ObserveDialog, ContactDialog, SourceDialog, SituationDialog, LinkDialog,
} from "@/components/reperes/ReperesDialogs";

const SOURCE_LABELS = { recommendation: tfr("Recommandation"), import: tfr("Import"), manual: tfr("Ajout manuel") };

function Card({ title, Icon, action, children, testid }) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 xl:p-3.5 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-xs xl:text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}{title}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ k, v, warn, testid }) {
  return (
    <div className="flex items-start justify-between gap-3" data-testid={testid}>
      <dt className="text-muted-foreground shrink-0">{k}</dt>
      <dd className={cn("text-right font-medium min-w-0", warn && "text-warning")}>{v}</dd>
    </div>
  );
}

export default function ReperesDetail({ p, tf, onChanged, onDiscard }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dlg, setDlg] = useState(null);
  const [history, setHistory] = useState(null);
  const noteRef = useRef(null);

  const loadDetail = useCallback(async () => {
    try {
      const r = await api.get(`/reperes/${p.id}`);
      setDetail(r.data);
    } catch { /* row data remains usable */ }
  }, [p.id]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => {
    api.get("/dashboard/activity", { params: { player_id: p.id, limit: 20 } })
      .then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  }, [p.id, detail]);

  const d = detail?.prospect || p;
  const contacts = detail?.contacts || [];
  const notes = detail?.notes || [];
  const obs = detail?.next_observation || null;
  const legalReps = contacts.filter((c) => c.is_legal_representative);

  const refresh = () => { loadDetail(); onChanged(); };

  const removeContact = async (cid) => {
    try { await api.delete(`/reperes/${p.id}/contacts/${cid}`); toast.success(tf("Interlocuteur supprimé")); loadDetail(); }
    catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };
  const removeLink = async (idx) => {
    try {
      await api.patch(`/reperes/${p.id}`, { links: (d.links || []).filter((_, i) => i !== idx) });
      toast.success(tf("Lien supprimé")); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };

  const TABS = [
    { id: "overview", label: tfr("Vue d'ensemble"), Icon: CircleHelp },
    { id: "notes", label: tfr("Notes"), Icon: StickyNote, count: notes.length },
    { id: "history", label: tfr("Historique"), Icon: HistoryIcon },
  ];

  return (
    <div className="rounded-2xl border-0 bg-transparent p-0 xl:border xl:border-border xl:bg-background/40 xl:p-4 space-y-5 xl:space-y-4 min-w-0" data-testid="reperes-detail">
      {/* Header */}
      <div className="flex items-start gap-3.5 xl:gap-3 min-w-0 pr-11 xl:pr-0">
        <Avatar className="h-16 w-16 xl:h-14 xl:w-14 shrink-0">
          <AvatarImage src={d.photo_url} alt="" />
          <AvatarFallback>{initialsOf(d.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[22px] leading-tight xl:text-lg font-bold truncate" data-testid="detail-name">{d.name}</span>
            {d.minor && <span className="text-[10px] font-semibold uppercase rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px" data-testid="detail-minor-badge">{tf("Mineur")}</span>}
          </div>
          <div className="text-[15px] xl:text-xs text-muted-foreground truncate">
            {[d.age ? `${d.age} ans` : null, d.position].filter(Boolean).join(" · ")}
          </div>
          <div className="text-[15px] xl:text-xs text-muted-foreground truncate">
            {[d.club || tf("Sans club"), d.nationality].filter(Boolean).join(" · ")}
          </div>
          <div className="text-[13px] xl:text-[11px] text-muted-foreground mt-1.5 xl:mt-1" data-testid="detail-discovered">
            {tf("Repéré le")} {fmtDate(d.discovered_at)} · {relDays(d.discovered_at, tf)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2" data-testid="detail-actions">
        <Button size="sm" className="bg-foreground hover:bg-foreground/90 text-background text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3"
          onClick={() => setDlg({ type: "contact" })} data-testid="action-first-contact">
          <Send className="h-3.5 w-3.5 mr-1.5" />{tf("Premier contact")}
        </Button>
        <Button variant="outline" size="sm" className="text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3"
          onClick={() => setDlg({ type: "observe" })} data-testid="action-observe">
          <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />{tf("Planifier une observation")}
        </Button>
        <Button variant="outline" size="sm" className="text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3 text-athlynx border-athlynx/30 hover:bg-athlynx/10"
          onClick={onDiscard} data-testid="action-discard">
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />{tf("Écarter")}
        </Button>
        <Button variant="ghost" size="sm"
          className="w-full xl:w-auto justify-start h-14 xl:h-9 text-sm xl:text-xs text-foreground xl:text-muted-foreground border-y border-border/60 xl:border-0 rounded-none xl:rounded-md px-1 xl:px-3"
          onClick={() => navigate(`/players/${p.id}`)} data-testid="detail-open-profile">
          <UserRound className="h-4 w-4 xl:h-3.5 xl:w-3.5 mr-2 xl:mr-1.5" />{tf("Profil complet")}
          <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground xl:hidden" aria-hidden="true" />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto scroll-thin" role="tablist" data-testid="detail-tabs">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id}
            onClick={() => setTab(t.id)} data-testid={`detail-tab-${t.id}`}
            className={cn("shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm xl:px-3 xl:py-2 xl:text-xs font-medium border-b-2 -mb-px transition-colors",
              tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
            <t.Icon className="h-3.5 w-3.5" />{tf(t.label)}
            {t.count > 0 && <span className="text-[10px] tabular-nums rounded-full border border-border px-1.5">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 min-[1700px]:grid-cols-2 gap-4" data-testid="detail-overview">
          {/* Pourquoi ce joueur ? */}
          <Card title={tf("Pourquoi ce joueur ?")} Icon={CircleHelp} testid="card-why"
            action={<button onClick={() => setDlg({ type: "source" })} className="text-muted-foreground hover:text-foreground" aria-label={tf("Modifier la source")} data-testid="edit-source-btn"><Pencil className="h-3.5 w-3.5" /></button>}>
            <dl className="space-y-2 text-sm xl:space-y-1.5 xl:text-xs">
              <Row k={tf("Source")} v={d.source_type ? tf(SOURCE_LABELS[d.source_type]) : tf("Source non renseignée")} testid="why-source" />
              {d.source_type === "recommendation" && d.source_label && <Row k={tf("Recommandé par")} v={d.source_label} testid="why-recommended-by" />}
              {d.source_type !== "recommendation" && d.source_label && <Row k={tf("Détail")} v={d.source_label} />}
              <Row k={tf("Date")} v={fmtDate(d.discovered_at)} />
            </dl>
            {d.source_notes && (
              <p className="text-xs text-foreground/85 italic mt-2 border-t border-border/60 pt-2" data-testid="why-context">« {d.source_notes} »</p>
            )}
          </Card>

          {/* Situation actuelle */}
          <Card title={tf("Situation actuelle")} Icon={Activity} testid="card-situation"
            action={<button onClick={() => setDlg({ type: "situation" })} className="text-muted-foreground hover:text-foreground" aria-label={tf("Modifier la situation")} data-testid="edit-situation-btn"><Pencil className="h-3.5 w-3.5" /></button>}>
            <dl className="space-y-2 text-sm xl:space-y-1.5 xl:text-xs">
              <Row k={tf("Club actuel")} v={d.club || tf("Sans club")} />
              <Row k={tf("Dernier club")} v={d.last_club || "—"} />
              <Row k={tf("Niveau")} v={d.level || "—"} />
              <Row k={tf("Fin de contrat")} testid="situation-contract"
                warn={d.contract_days_left !== null && d.contract_days_left <= 180}
                v={d.contract_end_date ? (
                  <span>
                    {fmtDate(d.contract_end_date)}
                    <span className={cn("ml-1.5 inline-block text-[10px] rounded-full border px-1.5 py-px",
                      d.contract_days_left < 0 ? "border-athlynx/40 text-athlynx" : "border-border text-muted-foreground")}>
                      {tf(d.contract_remaining_label || "")}
                    </span>
                  </span>
                ) : tf("Non renseignée")} />
              <Row k={tf("Nationalité")} v={d.nationality || "—"} />
            </dl>
          </Card>

          {/* Interlocuteurs */}
          <Card title={tf("Interlocuteurs")} Icon={ContactRound} testid="card-interlocutors"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => setDlg({ type: "add-contact" })} data-testid="add-contact-btn"><Plus className="h-3 w-3 mr-1" />{tf("Ajouter")}</Button>}>
            {d.minor && legalReps.length === 0 && (
              <div className="rounded-lg border border-warning/30 bg-warning/[0.06] p-2 mb-2 text-[11px] text-warning flex items-center gap-1.5" data-testid="no-legal-rep-warning">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0" />{tf("Aucun représentant légal renseigné.")}
              </div>
            )}
            {contacts.length === 0 ? (
              <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun interlocuteur renseigné.")}</p>
            ) : (
              <ul className="space-y-2.5">
                {contacts.map((c) => (
                  <li key={c.id} className="min-w-0" data-testid={`contact-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-semibold">{c.name}</span>
                          {c.is_legal_representative && (
                            <span className="text-[10px] font-semibold rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px" data-testid={`legal-rep-badge-${c.id}`}>{tf("Représentant légal")}</span>
                          )}
                        </div>
                        {c.relationship && <div className="text-[11px] text-muted-foreground">{tf(c.relationship)}</div>}
                        {c.phone && <div className="text-[11px] text-muted-foreground tabular-nums">{c.phone}</div>}
                        {c.email && <div className="text-[11px] text-muted-foreground truncate">{c.email}</div>}
                        <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                          {tf("Source")} : {c.source || tf("non renseignée")}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {c.phone && (
                          <a href={`https://wa.me/${c.phone.replace(/[^0-9+]/g, "")}`} target="_blank" rel="noreferrer"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60 transition-colors"
                            aria-label={`WhatsApp ${c.name}`} data-testid={`contact-wa-${c.id}`}>
                            <MessageCircle className="h-3.5 w-3.5 text-muted-foreground" /></a>
                        )}
                        {c.phone && (
                          <a href={`tel:${c.phone}`}
                            className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60 transition-colors"
                            aria-label={`${tf("Appeler")} ${c.name}`} data-testid={`contact-call-${c.id}`}>
                            <Phone className="h-3.5 w-3.5 text-muted-foreground" /></a>
                        )}
                        {c.email && (
                          <a href={`mailto:${c.email}`}
                            className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60 transition-colors"
                            aria-label={`Email ${c.name}`} data-testid={`contact-email-${c.id}`}>
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" /></a>
                        )}
                        <button onClick={() => removeContact(c.id)}
                          className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-muted-foreground/60 hover:text-athlynx transition-colors"
                          aria-label={`${tf("Supprimer")} ${c.name}`} data-testid={`contact-delete-${c.id}`}>
                          <X className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Prochain match / Observation */}
          <Card title={tf("Prochain match / Observation")} Icon={CalendarDays} testid="card-observation">
            {obs ? (
              <div className="space-y-1 text-sm xl:text-xs" data-testid="next-observation">
                <div className="font-semibold">{obs.title}</div>
                <div className="text-muted-foreground tabular-nums">
                  {new Date(obs.starts_at).toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                  {" · "}{new Date(obs.starts_at).toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" })}
                </div>
                {obs.location && <div className="text-muted-foreground">{obs.location}</div>}
                <div className="flex gap-1.5 pt-1.5">
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => navigate("/calendar")} data-testid="obs-open-calendar">{tf("Voir le calendrier")}</Button>
                  <CancelObservation tf={tf} obs={obs} onDone={refresh} />
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground" data-testid="no-observation">
                {tf("Aucun prochain match renseigné")}
                <div className="pt-2">
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setDlg({ type: "observe" })} data-testid="obs-plan-btn">
                    <CalendarPlus className="h-3 w-3 mr-1" />{tf("Planifier une observation")}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Notes récentes */}
          <Card title={tf("Notes récentes")} Icon={StickyNote} testid="card-notes"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => { setTab("notes"); setTimeout(() => noteRef.current?.focus(), 50); }} data-testid="notes-add-btn"><Plus className="h-3 w-3 mr-1" />{tf("Ajouter")}</Button>}>
            {notes.length === 0 ? (
              <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucune note pour le moment.")}</p>
            ) : (
              <ul className="space-y-2">
                {notes.slice(0, 3).map((n) => (
                  <li key={n.id} className="text-xs">
                    <p className="text-foreground/85">« {n.note} »</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{relDays(n.occurred_at, tf)}</p>
                  </li>
                ))}
              </ul>
            )}
            {notes.length > 3 && (
              <button onClick={() => setTab("notes")} className="text-[11px] text-info hover:underline mt-2" data-testid="notes-see-all">
                {tf("Voir toutes les notes")} ({notes.length})
              </button>
            )}
          </Card>

          {/* Liens utiles */}
          <Card title={tf("Liens utiles")} Icon={Link2} testid="card-links"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => setDlg({ type: "link" })} data-testid="add-link-btn"><Plus className="h-3 w-3 mr-1" />{tf("Ajouter")}</Button>}>
            {(d.links || []).length === 0 ? (
              <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun lien ajouté.")}</p>
            ) : (
              <ul className="space-y-1">
                {d.links.map((l, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-info hover:underline min-w-0 truncate" data-testid={`link-${i}`}>
                      <ExternalLink className="h-3 w-3 shrink-0" /><span className="truncate">{l.label}</span>
                    </a>
                    <button onClick={() => removeLink(i)} className="text-muted-foreground/60 hover:text-athlynx shrink-0" aria-label={`${tf("Supprimer")} ${l.label}`} data-testid={`link-delete-${i}`}>
                      <X className="h-3 w-3" /></button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Activité récente */}
          <Card title={tf("Activité récente")} Icon={HistoryIcon} testid="card-recent-activity">
            {!history || history.length === 0 ? (
              <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucune activité enregistrée.")}</p>
            ) : (
              <ul className="space-y-1.5">
                {history.slice(0, 3).map((h) => (
                  <li key={h.id} className="text-xs flex items-start justify-between gap-2">
                    <span className="text-foreground/85 min-w-0">{h.title}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{relDays(h.at, tf)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {tab === "notes" && <NotesTab p={p} tf={tf} notes={notes} noteRef={noteRef} onSaved={loadDetail} />}

      {tab === "history" && (
        <div className="space-y-2" data-testid="detail-history">
          {!history ? (
            <div className="h-20 rounded-xl bg-secondary/60 animate-pulse" />
          ) : history.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center">{tf("Aucun événement dans l'historique.")}</p>
          ) : (
            <ul className="space-y-2.5">
              {history.map((h) => (
                <li key={h.id} className="flex items-start gap-2.5 text-xs" data-testid={`history-item-${h.id}`}>
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-foreground/50 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground/90">{h.title}</p>
                    {h.subtitle && <p className="text-[11px] text-muted-foreground">{h.subtitle}</p>}
                    <p className="text-[10px] text-muted-foreground/70">{fmtDate(h.at)} · {relDays(h.at, tf)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {dlg?.type === "contact" && <FirstContactDialog p={d} contacts={contacts} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} onAddContact={() => setDlg({ type: "add-contact" })} />}
      {dlg?.type === "observe" && <ObserveDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "add-contact" && <ContactDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); loadDetail(); }} />}
      {dlg?.type === "source" && <SourceDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "situation" && <SituationDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "link" && <LinkDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
    </div>
  );
}

function CancelObservation({ tf, obs, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const cancel = async () => {
    try { await api.delete(`/events/${obs.id}`); toast.success(tf("Observation annulée")); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };
  return confirming ? (
    <Button variant="outline" size="sm" className="h-7 text-[11px] text-athlynx border-athlynx/30" onClick={cancel} data-testid="obs-cancel-confirm">
      {tf("Confirmer l'annulation ?")}
    </Button>
  ) : (
    <Button variant="ghost" size="sm" className="h-7 text-[11px] text-muted-foreground" onClick={() => setConfirming(true)} data-testid="obs-cancel-btn">
      {tf("Annuler")}
    </Button>
  );
}

function NotesTab({ p, tf, notes, noteRef, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/interactions", { player_id: p.id, type: "note", note: text.trim() });
      setText(""); toast.success(tf("Note ajoutée")); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="detail-notes-tab">
      <div className="space-y-2">
        <Textarea ref={noteRef} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={tf("Ajouter une note sur ce prospect…")} className="text-xs min-h-[70px]" data-testid="note-input" />
        <Button size="sm" disabled={busy || !text.trim()} onClick={save}
          className="bg-foreground hover:bg-foreground/90 text-background text-xs h-8" data-testid="note-save-btn">
          {tf("Enregistrer la note")}
        </Button>
      </div>
      {notes.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">{tf("Aucune note pour le moment.")}</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((n) => (
            <li key={n.id} className="rounded-xl border border-border bg-card p-3 text-xs" data-testid={`note-${n.id}`}>
              <p className="text-foreground/90 whitespace-pre-wrap">{n.note}</p>
              <p className="text-[10px] text-muted-foreground mt-1.5">{fmtDate(n.occurred_at)} · {relDays(n.occurred_at, tf)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
